import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Download } from 'lucide-react'
import { billingApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import StatusBadge from '../../components/shared/StatusBadge'
import FilterBar from '../../components/shared/FilterBar'
import StatCard from '../../components/shared/StatCard'
import Pagination from '../../components/shared/Pagination'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDate, formatMoney } from '../../lib/utils'
import type { BillingInvoice, MoneyLog } from '../../types'
import { DollarSign, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react'
import { tx } from '../../lib/i18n'

const invoiceStatusOptions = [
  { label: tx('Paid'), value: 'paid' },
  { label: tx('Pending'), value: 'pending' },
  { label: tx('Failed'), value: 'failed' },
  { label: tx('Cancelled'), value: 'cancelled' },
  { label: tx('Refunded'), value: 'refunded' },
]

type BillingPaymentRow = {
  id: string
  shopId: string
  shopName?: string
  amount: number | string
  currency: string
  paymentMethod: string | null
  status: string
  reference: string | null
  paidAt: string | null
  createdAt: string
}

const BillingPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [activeTab, setActiveTab] = useState<'invoices' | 'payments' | 'adjustments'>('invoices')
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [paymentModal, setPaymentModal] = useState(false)
  const [adjustmentModal, setAdjustmentModal] = useState(false)

  const [paymentForm, setPaymentForm] = useState({ shopId: '', amount: '', paymentMethod: 'bank_transfer', reference: '', reason: '' })
  const [adjForm, setAdjForm] = useState({ shopId: '', type: 'credit' as 'credit' | 'debit', amount: '', reason: '' })

  const { data: invoicesData, isLoading: invLoading } = useQuery({
    queryKey: ['invoices', { page, search, statusFilter }],
    queryFn: () => billingApi.listInvoices({ page, limit: 20, search, status: statusFilter || undefined }).then((r) => r.data),
  })

  // Payments tab = real payment records (manual payments etc.), not the invoice list.
  const { data: paymentsData, isLoading: payLoading } = useQuery({
    queryKey: ['billing-payments', { page, statusFilter }],
    queryFn: () => billingApi.listPayments({ page, limit: 20, status: statusFilter || undefined }).then((r) => r.data as unknown as { data: BillingPaymentRow[]; total: number; totalPages: number }),
    enabled: activeTab === 'payments',
  })

  const { data: summaryData } = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => billingApi.getSummary().then((r) => r.data.data),
  })

  const { data: adjustmentsData } = useQuery({
    queryKey: ['billing-adjustments'],
    queryFn: () => billingApi.listAdjustments({ limit: 50 }).then((r) => r.data),
  })

  const manualPaymentMutation = useMutation({
    mutationFn: () => billingApi.addManualPayment({
      shopId: paymentForm.shopId,
      amount: parseFloat(paymentForm.amount),
      method: paymentForm.paymentMethod,
      reference: paymentForm.reference,
      reason: paymentForm.reason,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['billing-payments'] })
      showToast({ type: 'success', message: tx('Manual payment recorded.') })
      setPaymentModal(false)
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to record payment.') }),
  })

  const adjustmentMutation = useMutation({
    mutationFn: () => billingApi.addAdjustment({
      shopId: adjForm.shopId,
      type: adjForm.type,
      amount: parseFloat(adjForm.amount),
      reason: adjForm.reason,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['billing-adjustments'] })
      showToast({ type: 'success', message: tx('Adjustment applied.') })
      setAdjustmentModal(false)
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to apply adjustment.') }),
  })

  const invoices = invoicesData?.data ?? []
  const total = invoicesData?.total ?? 0
  const totalPages = invoicesData?.totalPages ?? 1
  const summary = summaryData ?? { totalPaid: 0, totalUnpaid: 0, overdue: 0, refunded: 0, mrr: 0 }

  const invoiceColumns: Column<BillingInvoice>[] = [
    { key: 'shopName', header: tx('Shop'), sortable: true, render: (r) => <span className="font-medium text-gray-900">{r.shopName}</span> },
    { key: 'invoiceNumber', header: tx('Invoice #'), render: (r) => <span className="font-mono text-xs text-gray-600">{r.invoiceNumber}</span> },
    { key: 'amount', header: tx('Amount'), render: (r) => <span className="font-medium text-gray-900">{formatMoney(r.amount, r.currency)}</span> },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={r.status} /> },
    { key: 'dueDate', header: tx('Due Date'), render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.dueDate)}</span> },
    { key: 'paidAt', header: tx('Paid At'), render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.paidAt)}</span> },
  ]

  const paymentColumns: Column<BillingPaymentRow>[] = [
    { key: 'shopName', header: tx('Shop'), render: (r) => <span className="font-medium text-gray-900">{r.shopName || r.shopId.slice(0, 8)}</span> },
    { key: 'reference', header: tx('Reference'), render: (r) => <span className="font-mono text-xs text-gray-600">{r.reference ?? '—'}</span> },
    { key: 'amount', header: tx('Amount'), render: (r) => <span className="font-medium text-gray-900">{formatMoney(Number(r.amount), r.currency)}</span> },
    { key: 'paymentMethod', header: tx('Payment Method'), render: (r) => <span className="text-sm text-gray-600">{r.paymentMethod ?? '—'}</span> },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={String(r.status).toLowerCase()} /> },
    { key: 'paidAt', header: tx('Paid At'), render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.paidAt ?? r.createdAt)}</span> },
  ]

  const adjColumns: Column<MoneyLog>[] = [
    { key: 'shopName', header: tx('Shop'), render: (r) => <span className="font-medium text-gray-900">{r.shopName}</span> },
    { key: 'type', header: tx('Type'), render: (r) => <StatusBadge status={r.type.includes('credit') ? 'active' : 'past_due'} label={r.type.replace(/_/g, ' ')} /> },
    { key: 'amount', header: tx('Amount'), render: (r) => <span className="font-medium">{formatMoney(r.amount, r.currency)}</span> },
    { key: 'description', header: tx('Reason'), render: (r) => <span className="text-gray-500 text-sm">{r.description ?? '—'}</span> },
    { key: 'actorAdmin', header: tx('Admin'), render: (r) => <span className="text-gray-600 text-sm">{r.actorAdmin ?? 'System'}</span> },
    { key: 'createdAt', header: tx('Date'), render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.createdAt)}</span> },
  ]

  const exportCSV = () => {
    const rows = [
      ['Invoice #', 'Shop', 'Amount', 'Status', 'Due Date', 'Paid At'],
      ...invoices.map((inv) => [inv.invoiceNumber, inv.shopName, inv.amount, inv.status, inv.dueDate ?? '', inv.paidAt ?? '']),
    ]
    const csv = rows.map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'billing-export.csv'
    a.click()
    URL.revokeObjectURL(url)
    showToast({ type: 'success', message: tx('CSV exported.') })
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Billing')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('Invoices, payments, and financial adjustments')}</p>
        </div>
        <div className="flex items-center gap-2">
          {can('billing.export') && (
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="h-4 w-4" />
            {tx('Export CSV')}
          </button>
          )}
          {can('billing.adjustment') && (
          <button
            onClick={() => setAdjustmentModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {tx('Adjustment')}
          </button>
          )}
          {can('billing.manual_payment') && (
          <button
            onClick={() => setPaymentModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {tx('Manual Payment')}
          </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title={tx('Total Paid')} value={formatMoney(summary.totalPaid)} icon={CheckCircle} iconColor="bg-green-100 text-green-600" />
        <StatCard title={tx('Total Unpaid')} value={formatMoney(summary.totalUnpaid)} icon={DollarSign} iconColor="bg-blue-100 text-blue-600" />
        <StatCard title={tx('Overdue')} value={formatMoney(summary.overdue)} icon={AlertTriangle} iconColor="bg-amber-100 text-amber-600" />
        <StatCard title={tx('Refunded')} value={formatMoney(summary.refunded)} icon={RefreshCw} iconColor="bg-purple-100 text-purple-600" />
        <StatCard title="MRR" value={formatMoney(summary.mrr)} icon={DollarSign} iconColor="bg-emerald-100 text-emerald-600" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {(['invoices', 'payments', 'adjustments'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setPage(1) }}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${
              activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tx('Search by shop...') }}
        filters={activeTab !== 'adjustments' ? [
          { key: 'status', label: tx('Status'), options: invoiceStatusOptions, value: statusFilter, onChange: setStatusFilter },
        ] : []}
        onReset={() => { setSearch(''); setStatusFilter(''); setPage(1) }}
      />

      <div>
        {activeTab === 'adjustments' ? (
          <DataTable columns={adjColumns} data={adjustmentsData?.data ?? []} emptyMessage={tx('No adjustments found.')} />
        ) : activeTab === 'payments' ? (
          <>
            <DataTable columns={paymentColumns} data={paymentsData?.data ?? []} isLoading={payLoading} emptyMessage="ยังไม่มีรายการชำระ" />
            <Pagination page={page} totalPages={paymentsData?.totalPages ?? 1} total={paymentsData?.total ?? 0} limit={20} onPageChange={setPage} />
          </>
        ) : (
          <>
            <DataTable columns={invoiceColumns} data={invoices} isLoading={invLoading} emptyMessage={tx('No invoices found.')} />
            <Pagination page={page} totalPages={totalPages} total={total} limit={20} onPageChange={setPage} />
          </>
        )}
      </div>

      {/* Manual Payment Modal */}
      {paymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPaymentModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Record Manual Payment')}</h3>
            <div className="space-y-3">
              {[
                { label: tx('Shop ID'), key: 'shopId', placeholder: 'shop-123' },
                { label: 'Amount (฿)', key: 'amount', placeholder: '990', type: 'number' },
                { label: tx('Reference'), key: 'reference', placeholder: tx('Bank transfer ref #') },
                { label: tx('Reason'), key: 'reason', placeholder: tx('Manual top-up') },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type={type ?? 'text'}
                    placeholder={placeholder}
                    value={(paymentForm as Record<string, string>)[key]}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, [key]: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Payment Method')}</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="bank_transfer">{tx('Bank Transfer')}</option>
                  <option value="cash">{tx('Cash')}</option>
                  <option value="credit_card">{tx('Credit Card')}</option>
                  <option value="promptpay">PromptPay</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setPaymentModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button
                onClick={() => manualPaymentMutation.mutate()}
                disabled={manualPaymentMutation.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {manualPaymentMutation.isPending ? tx('Saving...') : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Modal */}
      {adjustmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAdjustmentModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Add Balance Adjustment')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Shop ID')}</label>
                <input type="text" placeholder="shop-123" value={adjForm.shopId} onChange={(e) => setAdjForm((f) => ({ ...f, shopId: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tx('Type')}</label>
                <div className="flex gap-3">
                  {(['credit', 'debit'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setAdjForm((f) => ({ ...f, type: t }))}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg border capitalize transition-colors ${adjForm.type === t ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (฿)</label>
                <input type="number" placeholder="500" value={adjForm.amount} onChange={(e) => setAdjForm((f) => ({ ...f, amount: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Reason')} <span className="text-red-500">*</span></label>
                <textarea rows={2} value={adjForm.reason} onChange={(e) => setAdjForm((f) => ({ ...f, reason: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" placeholder={tx('Reason for adjustment...')} />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setAdjustmentModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button
                onClick={() => adjustmentMutation.mutate()}
                disabled={!adjForm.reason.trim() || adjustmentMutation.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {adjustmentMutation.isPending ? tx('Saving...') : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BillingPage
